import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Hash, Phone, Upload, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { UPLOAD_BUKTI_PAGE } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UploadBukti() {
  const params = new URLSearchParams(window.location.search);
  const fileInputRef = useRef(null);

  const [noPendaftaran, setNoPendaftaran] = useState(params.get("no") || "");
  const [whatsapp, setWhatsapp] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/jpg"].includes(f.type)) {
      setError("Bukti transfer harus berupa gambar JPG/PNG");
      return;
    }
    setError("");
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!noPendaftaran || !whatsapp || !file) {
      setError("Mohon lengkapi No. Pendaftaran, No. WhatsApp, dan bukti transfer.");
      return;
    }
    const formData = new FormData();
    formData.append("no_pendaftaran", noPendaftaran.trim());
    formData.append("whatsapp", whatsapp.trim());
    formData.append("bukti_transfer", file);

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/registrations/upload-bukti`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccess(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Gagal upload bukti transfer.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid={UPLOAD_BUKTI_PAGE.successPage}
        className="min-h-screen bg-black text-white flex items-center justify-center px-4 py-16"
      >
        <div className="max-w-md w-full text-center bg-zinc-900/80 border border-orange-500/30 rounded-2xl p-8">
          <CheckCircle2 className="w-16 h-16 text-orange-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Anton', sans-serif" }}>
            BUKTI TRANSFER TERKIRIM!
          </h2>
          <p className="text-white/60 text-sm mb-6">
            Terima kasih, {success.nama}. Bukti transfer Anda untuk No. Pendaftaran{" "}
            <span className="text-orange-400 font-semibold">{success.no_pendaftaran}</span> sedang menunggu
            verifikasi panitia.
          </p>
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-500 rounded-full font-bold px-6 py-3 text-sm transition-colors"
          >
            Kembali ke Beranda
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={UPLOAD_BUKTI_PAGE.page} className="min-h-screen bg-black text-white flex items-center justify-center px-4 py-16">
      <div className="max-w-md w-full">
        <h1 className="text-2xl font-bold mb-2 text-center" style={{ fontFamily: "'Anton', sans-serif" }}>
          UPLOAD BUKTI TRANSFER
        </h1>
        <p className="text-white/50 text-sm text-center mb-8">
          Untuk peserta yang sudah daftar dan ingin melengkapi bukti pembayaran.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label className="text-white/90 flex items-center gap-2">
              <Hash className="w-4 h-4 text-orange-400" /> No. Pendaftaran
            </Label>
            <Input
              data-testid={UPLOAD_BUKTI_PAGE.noPendaftaranInput}
              value={noPendaftaran}
              onChange={(e) => setNoPendaftaran(e.target.value)}
              placeholder="JBV5-XXXXX"
              className="bg-zinc-900 border-white/15 text-white placeholder:text-white/30"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-white/90 flex items-center gap-2">
              <Phone className="w-4 h-4 text-orange-400" /> No. WhatsApp
            </Label>
            <Input
              data-testid={UPLOAD_BUKTI_PAGE.whatsappInput}
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              placeholder="08xxxxxxxxxx (saat daftar)"
              className="bg-zinc-900 border-white/15 text-white placeholder:text-white/30"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-white/90 flex items-center gap-2">
              <Upload className="w-4 h-4 text-orange-400" /> Bukti Transfer (JPG/PNG)
            </Label>
            <input
              data-testid={UPLOAD_BUKTI_PAGE.fileInput}
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/jpg"
              onChange={handleFileChange}
              className="block w-full text-sm text-white/70 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-orange-600 file:text-white file:font-semibold hover:file:bg-orange-500 file:cursor-pointer cursor-pointer bg-zinc-900 border border-white/15 rounded-xl"
            />
            {preview && (
              <img
                data-testid={UPLOAD_BUKTI_PAGE.filePreview}
                src={preview}
                alt="Preview bukti transfer"
                className="mt-2 max-h-48 rounded-xl border border-white/10"
              />
            )}
          </div>

          {error && (
            <div data-testid={UPLOAD_BUKTI_PAGE.errorAlert} className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl px-4 py-3 text-sm">
              {error}
            </div>
          )}

          <Button
            data-testid={UPLOAD_BUKTI_PAGE.submitButton}
            type="submit"
            disabled={submitting}
            className="w-full bg-orange-600 hover:bg-orange-500 rounded-full font-bold text-base py-6"
          >
            {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : null}
            Kirim Bukti Transfer
          </Button>
        </form>
      </div>
    </div>
  );
}
